/* eslint-disable check-file/filename-blocklist -- pre-existing .jsx; this change only swaps an icon import, converting to .tsx is a separate migration */
import { useKFlags } from '@/hooks/use-k-flags';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  IconButton,
  Box,
  Input,
  Checkbox,
  Text,
  Flex,
  Button,
  Tooltip,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { Link01 } from '@kula-ai/icons-core';

// URL with query params
export const URL_REGEX =
  /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;

export const Link = ({ editor = {}, className, isFlow = false }) => {
  const inputRef = useRef();
  const selectedLink = editor?.getAttributes('link');
  const { href = '', target = '' } = selectedLink || {};
  const newTabRef = useRef(null);
  const [error, setError] = useState(null);
  const [value, setValue] = useState('');
  const [checked, setChecked] = useState(false);
  const handleChange = (event) => setValue(event.target.value);
  const handleNewTabChange = () => setChecked((prev) => !prev);
  const { ats_enabled } = useKFlags();

  const insertLink = () => {
    try {
      const link = value?.trim(); // Trim whitespace
      if (!link) {
        setError('Link cannot be empty');
        return;
      }
      if (!URL_REGEX.test(link)) {
        setError('Invalid URL');
        return;
      }

      // Normalize the link to use https://
      let normalizedLink = link;
      if (
        !normalizedLink.startsWith('http://') &&
        !normalizedLink.startsWith('https://')
      ) {
        // If no protocol is provided, add https://
        normalizedLink = `https://${normalizedLink}`;
      } else if (normalizedLink.startsWith('http://')) {
        // Replace http:// with https://
        normalizedLink = normalizedLink.replace('http://', 'https://');
      }

      setError(null);
      editor
        ?.chain()
        ?.focus()
        ?.extendMarkRange('link')
        .setLink({ href: normalizedLink, target: checked ? '_blank' : '_self' })
        .run();
    } catch {
      // editor chain calls can throw if the mark range is stale; ignore and leave the popover open
    }
  };

  const onOpen = () => {
    setTimeout(function () {
      inputRef?.current?.focus();
    }, 10);
    if (!href) {
      setValue('');
      return;
    }
    setValue(href);
    setChecked(target === '_blank');
  };

  if (!editor) return null;

  return (
    <Box pos="relative">
      <Popover onOpen={onOpen} placement="bottom-end">
        <Tooltip
          label="Link"
          arrowSize={8}
          hasArrow
          placement="top"
          borderRadius="4px"
          variant={ats_enabled && 'ats-primary'}
        >
          <Box display="inline-block">
            <PopoverTrigger>
              <IconButton
                height="auto"
                bg="inherit"
                _hover={{ bg: 'inherit' }}
                borderRadius="6px"
                cursor="pointer"
                py="8px"
                px="0px"
              >
                <Link01
                  color={isFlow && '#121314'}
                  className={className}
                  size="16px"
                />
              </IconButton>
            </PopoverTrigger>
          </Box>
        </Tooltip>
        <PopoverContent width="300px" p="8px">
          <PopoverArrow />
          <PopoverBody>
            <Box>
              <Text size="sm-medium">URL</Text>
              <Input
                ref={inputRef}
                mt="4px"
                placeholder="https://www.kula.ai"
                value={value}
                onChange={handleChange}
              />
            </Box>
            <Flex mt="12px" alignItems="center">
              <Checkbox
                type="checkbox"
                ref={newTabRef}
                defaultChecked={target === '_blank'}
                isChecked={checked}
                onChange={handleNewTabChange}
              >
                <Text>Open in new tab</Text>
              </Checkbox>
            </Flex>
            {error && (
              <Text mt="12px" color="#E53E3E">
                {error}
              </Text>
            )}
            <Flex mt="12px" justifyContent="end">
              <Button onClick={insertLink}>Insert</Button>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
